import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { CategoryFolder } from "~/api/model";
import {
  getListOrganizationCategoryFoldersQueryKey,
  useUpdateOrganizationCategoryFolder,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";

/**
 * EditFolderModal allows users to edit an existing folder's name and description.
 *
 * Features:
 * - Edit folder name (required field)
 * - Edit folder description (optional field)
 * - Form validation with error toasts
 *
 * Server State Management:
 * - Uses React Query's useUpdateOrganizationCategoryFolder mutation to update folder
 * - Invalidates getListOrganizationCategoryFoldersQueryKey after successful update
 *
 * UX Notes:
 * - Color is NOT editable in this modal; users change color via the color circle in FolderListItem
 * - Form state syncs with folder prop via useEffect when folder changes
 * - Modal closes automatically on successful update
 */
interface EditFolderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: CategoryFolder;
}

export default function EditFolderModal({
  open,
  onOpenChange,
  folder,
}: EditFolderModalProps) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: updateFolder, isPending } =
    useUpdateOrganizationCategoryFolder();
  const { toast } = useToast();
  const intl = useIntl();
  const [formData, setFormData] = useState({
    name: folder.name,
    description: folder.description || "",
  });

  useEffect(() => {
    setFormData({
      name: folder.name,
      description: folder.description || "",
    });
  }, [folder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.nameRequired" }),
      });
      return;
    }

    try {
      await updateFolder({
        organizationId: organizationId,
        folderId: folder.id,
        data: {
          name: formData.name,
          description: formData.description || undefined,
        },
      });

      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
      });

      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "folder.updateError" }),
      });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={intl.formatMessage({ id: "folder.edit.title" })}
      description={intl.formatMessage({ id: "folder.edit.description" })}
      content={
        <form onSubmit={handleSubmit} id="edit-folder-form">
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="folder-name">
                {intl.formatMessage({ id: "folder.name" })}
              </Label>
              <Input
                id="folder-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "folder.namePlaceholder",
                })}
                className="shadow-none"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="folder-description">
                {intl.formatMessage({ id: "folder.description" })}
              </Label>
              <AutoResizeTextarea
                id="folder-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "folder.descriptionPlaceholder",
                })}
              />
            </div>
          </div>
        </form>
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button type="submit" form="edit-folder-form" disabled={isPending}>
            <FormattedMessage id="save" />
          </Button>
        </>
      }
    />
  );
}
